/**
 * Central Validation Rules Registry
 * Issue #1101: Centralized live validation system
 *
 * Like CSS where you define styles centrally, validation rules are defined once
 * and any input can use them by type.
 */

import { extractInviteCode } from '../inviteCode';

export interface ValidationRule {
  // Pattern validation
  pattern?: RegExp;
  // Length constraints
  minLength?: number;
  maxLength?: number;
  // Numeric constraints
  min?: number;
  max?: number;
  // Custom validator function
  validate?: (value: string) => boolean;
  // Error message
  message: string;
  // Success message (optional)
  successMessage?: string;
}

/**
 * Central validation rules - define once, use everywhere
 */
export const validationRules: Record<string, ValidationRule> = {
  // Email validation
  email: {
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    message: 'Enter a valid email address',
  },

  // Club name (3-50 chars)
  clubName: {
    minLength: 3,
    maxLength: 50,
    message: '3–50 characters',
  },

  // Event title (3-100 chars)
  eventTitle: {
    minLength: 3,
    maxLength: 100,
    message: '3–100 characters',
  },

  // Short description (max 200 chars)
  shortDescription: {
    maxLength: 200,
    message: 'Maximum 200 characters',
  },

  // Long description (max 2000 chars, standard punctuation)
  description: {
    maxLength: 2000,
    // Allow letters, numbers, whitespace, and standard punctuation
    pattern: /^[\p{L}\p{N}\s.,!?'"\-():;@#$%&*+=[\]{}|\\/<>~`_^]*$/u,
    message: 'Letters, numbers, and standard punctuation only',
  },

  // URL validation
  url: {
    pattern: /^https?:\/\/.+/,
    message: 'Enter a valid URL (https://...)',
  },

  // Distance (positive number with up to 2 decimal places)
  distance: {
    pattern: /^\d+(\.\d{1,2})?$/,
    validate: (value) => {
      const num = parseFloat(value);
      return !isNaN(num) && num >= 0;
    },
    message: 'Enter a valid distance',
  },

  // Duration in minutes (positive integer)
  duration: {
    pattern: /^\d+$/,
    validate: (value) => {
      const num = parseInt(value, 10);
      return !isNaN(num) && num >= 1;
    },
    message: 'Enter a valid duration',
  },

  // Phone number (basic format)
  phone: {
    pattern: /^[+]?[\d\s\-().]{7,20}$/,
    message: 'Enter a valid phone number',
  },

  // Username/slug (lowercase, alphanumeric, hyphens)
  slug: {
    pattern: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
    minLength: 3,
    maxLength: 50,
    message: 'Lowercase letters, numbers, and hyphens (3–50 characters)',
  },

  // Secret word (uppercase letters only)
  secretWord: {
    pattern: /^[A-Z]+$/,
    minLength: 3,
    maxLength: 20,
    message: 'Uppercase letters only (3–20 characters)',
  },

  // Location name
  locationName: {
    maxLength: 100,
    message: 'Maximum 100 characters',
  },

  // Pace group name (1-50 chars)
  paceGroupName: {
    minLength: 1,
    maxLength: 50,
    message: 'Group name required (max 50 characters)',
  },

  // Pace value (e.g., "6:00/km", "20 mph")
  paceValue: {
    maxLength: 30,
    message: 'Maximum 30 characters',
  },

  // Address
  address: {
    maxLength: 200,
    message: 'Maximum 200 characters',
  },

  // Generic required field
  required: {
    minLength: 1,
    message: 'This field is required',
  },

  // Invite link or bare code (#2323 zero-club Welcome paste box).
  // Delegates to the same extractor the submit path uses, so the inline
  // validity signal and the navigation decision can never disagree.
  inviteInput: {
    validate: (value: string) => extractInviteCode(value) !== null,
    message: 'Paste an invite link or code (like ABCD2345)',
  },
};

/**
 * Validate a value against a rule type
 */
export function validateValue(
  value: string,
  ruleType: keyof typeof validationRules
): { isValid: boolean; message: string | null } {
  const rule = validationRules[ruleType];
  if (!rule) {
    return { isValid: true, message: null };
  }

  // Empty values pass validation (use 'required' rule to enforce)
  if (!value || value.trim() === '') {
    return { isValid: true, message: null };
  }

  const trimmedValue = value.trim();

  // Check minLength
  if (rule.minLength !== undefined && trimmedValue.length < rule.minLength) {
    return { isValid: false, message: rule.message };
  }

  // Check maxLength
  if (rule.maxLength !== undefined && trimmedValue.length > rule.maxLength) {
    return { isValid: false, message: rule.message };
  }

  // Check pattern
  if (rule.pattern && !rule.pattern.test(trimmedValue)) {
    return { isValid: false, message: rule.message };
  }

  // Check custom validator
  if (rule.validate && !rule.validate(trimmedValue)) {
    return { isValid: false, message: rule.message };
  }

  // Check numeric constraints
  if (rule.min !== undefined || rule.max !== undefined) {
    const num = parseFloat(trimmedValue);
    if (isNaN(num)) {
      return { isValid: false, message: rule.message };
    }
    if (rule.min !== undefined && num < rule.min) {
      return { isValid: false, message: rule.message };
    }
    if (rule.max !== undefined && num > rule.max) {
      return { isValid: false, message: rule.message };
    }
  }

  return { isValid: true, message: rule.successMessage || null };
}

export type ValidationRuleType = keyof typeof validationRules;
